<template>
  <div>
    <template v-if="!loading">
      <list-filter
        :attribute-list-selected="attributeListSelected"
        :attribute-list-showable="attributeListShowable"
        :entity-list-filtered="entityListFiltered"
        :entity-list-formatted="entityListFormatted"
        :entity-list-selected="entityListSelected"
        :entity-name="entityName"
        :filter-mode="filterMode"
        :id-attribute-name="idAttributeName"
        :import-key-attribute="importKeyAttribute"
        @update-attribute-list-selected="updateAttributeListSelected"
        @update-entity-list-filtered="updateEntityListFiltered"
        @update-entity-list-selected="updateEntityListSelected"
      ></list-filter>

      <attribute-selector
        :attribute-list-selected="attributeListSelected"
        :attribute-list-showable="attributeListShowable"
        :entity-name="entityName"
        @update-attribute-list-selected="updateAttributeListSelected"
      ></attribute-selector>

      <list-actions
        :build-is-closed="buildIsClosed"
        :entity-list-filtered="entityListFiltered"
        :entity-list-selected="entityListSelected"
        :entity-name="entityName"
        :id-attribute-name="idAttributeName"
      ></list-actions>

      <list-table
        :attribute-list-selected="attributeListSelected"
        :attribute-list="attributeList"
        :entity-list-filtered="entityListFiltered"
        :entity-list-selected="entityListSelected"
        :entity-name="entityName"
        :id-attribute-name="idAttributeName"
        @update-entity-list-selected="updateEntityListSelected"
      ></list-table>

      <v-card class="elevation-0" data-cy="listTable-addEntityTip">
        <v-card-text>
          <v-icon large left>fas fa-info-circle</v-icon
          ><template v-if="!entityList.length"
            >There are currently no {{ entityName }}s for your Service Area. </template
          >You can add {{ entityName }}s by clicking the “Import File” button
          for importing via CSV, or you can create {{ entityName }}s one at a
          time using the "Add” button.</v-card-text
        >
      </v-card>
    </template>
    <generic-loader v-else></generic-loader>
  </div>
</template>

<script>
import { clone, get, has, keyBy, memoize, reduce, uniq } from "lodash";
import { mapActions, mapGetters, mapState } from "vuex";
import { props } from "bluebird";

import { resolveAttributeLabel } from "@/lib/resolver";
import AttributeSelector from "./components/list/AttributeSelector.vue";
import GenericLoader from "@/components/GenericLoader";
import ListActions from "./components/list/ListActions.vue";
import ListFilter from "./components/list/ListFilter.vue";
import ListTable from "./components/list/ListTable.vue";

export default {
  components: {
    AttributeSelector,
    GenericLoader,
    ListActions,
    ListFilter,
    ListTable,
  },
  computed: {
    attributeList() {
      const { entityName } = this;

      const attributeList = clone(this.attributeFilter({ entityName }));

      return attributeList
    },
    attributeListShowable() {
      // Clone to avoid accidentally mutating reference with methods like Array.sort()
      const list = clone(this.attributeList);

      // Attributes without an elementType cannot be rendered
      const listWithElementType = list
        .filter(({ elementType, view }) => !!elementType || view);

        const listWithCareSetting = listWithElementType.filter(attribute => {
          if (
            this.organization.careSetting === "Acute Care" &&
            has(attribute, "acute")
          ) {
            return !!attribute.acute;
          }
          if (
            this.organization.careSetting === "Ambulatory" &&
            has(attribute, "ambulatory")
          ) {
            return !!attribute.ambulatory;
          }
          return true;
        });

      listWithCareSetting.sort(
        ({ orderNumber: orderNumber1 }, { orderNumber: orderNumber2 }) =>
          orderNumber1 >= orderNumber2
      );

      return listWithCareSetting;
    },
    attributeMap() {
      return keyBy(this.attributeList, "name");
    },
    buildIsClosed() {
      return this.organizationStatusIsGreaterThanOrEqualTo("buildClosed");
    },
    entityList() {
      const { $store, entityName } = this;

      return get($store, `state.${entityName}.list`, []);
    },
    idAttribute() {
      return this.attributeList.find(({ primaryKey }) => !!primaryKey);
    },
    idAttributeName() {
      const { idAttribute } = this;

      if (!idAttribute) {
        throw new Error(
          `No primary key attribute found for ${this.entityName}`
        );
      }

      const { name: idAttributeName } = idAttribute;

      return idAttributeName;
    },
    importKeyAttribute() {
      return this.attributeList.find(({ importKey }) => !!importKey);
    },
    organization() {
      const { getOrganizationById, organizationId } = this;
      if (!organizationId) {
        return;
      }
      return getOrganizationById(organizationId);
    },
    ...mapGetters({
      attributeFilter: "attribute/filter",
      getOrganizationById: "organization/getById",
      organizationStatusIsGreaterThanOrEqualTo:
        "organizationStatus/organizationStatusIsGreaterThanOrEqualTo",
    }),
    ...mapState({
      organizationId: state => state.session.organizationId,
    }),
  },
  async created() {
    const { entityName } = this;

    await this.init(entityName);

    const { attributeListShowable, attributeListSelected } = this;

    const importKeyAttributeList = attributeListShowable.filter(
      ({ importKey }) => !!importKey
    );
    const showDefaultAttributeList = attributeListShowable.filter(
      ({ showDefault }) => !!showDefault
    );
    const startingAttributeList = uniq(
      attributeListSelected.concat(
        importKeyAttributeList,
        showDefaultAttributeList
      ),
      "name"
    );

    this.updateAttributeListSelected(startingAttributeList);
  },
  data() {
    return {
      attributeListSelected: [],
      entityListFiltered: [],
      entityListFormatted: [],
      entityListSelected: [],
      loading: false,
    };
  },
  methods: {
    async init() {
      this.loading = true;

      let entityListFormatted;
      try {
        // Load entity data
        await Promise.all([
          this.attributeGetList({ reload: false }),
          this.entityGetList({ reload: false }),
          this.loadOrganization(),
        ]);

        entityListFormatted = await this.formatEntityList(
          memoize(
            resolveAttributeLabel,
            (value, attribute) => `${value}:${attribute.name}`
          ),
          clone(this.entityList),
          keyBy(this.attributeMap, "name")
        );
      } catch (e) {
        console.error(e);

        this.loading = false;
        this.$notifyError({ message: `Error loading ${this.entityName} data` });

        return;
      }

      this.loading = false;

      this.entityListFormatted = entityListFormatted;
      this.attributeListSelected = this.attributeListShowable.filter(
        ({ showDefault }) => !!showDefault
      );
    },
    async formatEntityList(formatValue, entityList, attributeMap) {
      return Promise.all(entityList.map(formatEntity));

      async function formatEntity(entity) {
        const entityFormatted = await props(
          reduce(entity, formatAttributeValue, {})
        );

        return entityFormatted;
      }

      // Can't be async function because it will wrap return acc in a promise
      function formatAttributeValue(acc, value, attributeName) {
        const attribute = attributeMap[attributeName];

        // If attribute cannot be found, it means this entity has deprecated attributes
        if (!attribute) {
          console.warn(
            `Unable to find attribute definition for ${attributeName}`
          );

          return acc;
        }

        const formattedValue = formatValue(value, attribute);

        acc[attributeName] = formattedValue;
        return acc;
      }
    },
    async entityGetList(optionMap) {
      return this.$store.dispatch(`${this.entityName}/list`, optionMap);
    },
    updateAttributeListSelected(updatedAttributeListSelected) {
      this.attributeListSelected = updatedAttributeListSelected;
    },
    updateEntityListFiltered(updatedEntityListFiltered) {
      this.entityListFiltered = updatedEntityListFiltered;
    },
    updateEntityListSelected(updatedEntityListSelected) {
      this.entityListSelected = updatedEntityListSelected;
    },
    ...mapActions({
      attributeGetList: "attribute/list",
      loadOrganization: "organization/list",
    }),
  },
  props: {
    entityName: {
      required: true,
      type: String,
    },
    filterMode: {},
  },
  watch: {
    async entityName(entityName) {
      return this.init(entityName);
    },
  },
};
</script>
